const MAX_SEEDS = 3;
const MAX_CARDS = 4;

// Both tile sources — the Section Rendering API's response and the curated <template> inside the
// element — ship the same `[data-recommendation][data-handle]` wrappers, so one collector serves
// both. `importNode` because the template's content lives in its own inert document too.
function collectRecommendationCards(root) {
  return [...root.querySelectorAll('[data-recommendation]')]
    .filter(wrapper => wrapper.dataset.handle && wrapper.querySelector('.horizontal-product'))
    .map(wrapper => ({
      handle: wrapper.dataset.handle,
      node: document.importNode(wrapper.querySelector('.horizontal-product'), true),
    }));
}

function parseRecommendationCards(html) {
  return collectRecommendationCards(new DOMParser().parseFromString(html, 'text/html'));
}

function deduplicateByHandle(cardEntries, excludedHandles) {
  const seenHandles = new Set(excludedHandles);
  return cardEntries
    .filter(({ handle }) => {
      if (seenHandles.has(handle)) return false;
      seenHandles.add(handle);
      return true;
    })
    .map(({ node }) => node);
}

function hexToRgbChannels(hexColor) {
  const hexMatch = hexColor?.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!hexMatch) return null;
  return `${parseInt(hexMatch[1], 16)} ${parseInt(hexMatch[2], 16)} ${parseInt(hexMatch[3], 16)}`;
}

function applyCardBackground(productCard, hexColor) {
  const rgbChannels = hexToRgbChannels(hexColor);
  if (!rgbChannels) return;
  productCard.classList.add('bg-custom', 'rounded-xs');
  productCard.style.setProperty('--background', rgbChannels);
}

function applyCardTextColor(productCard, hexColor) {
  const rgbChannels = hexToRgbChannels(hexColor);
  if (!rgbChannels) return;
  productCard.classList.add('text-custom');
  productCard.style.setProperty('--text-color', rgbChannels);
}

// The rail's one and only shell, for API results and curated tiles alike. Arrows only when there
// is more than one card to scroll to.
function buildCarouselShell(carouselId, themeStrings, showArrows) {
  const previousLabel = themeStrings.accessibilityPrevious ?? 'Previous';
  const nextLabel = themeStrings.accessibilityNext ?? 'Next';
  const arrows = showArrows
    ? `<div class="h-stack hidden gap-2 sm:flex">
        <button is="prev-button" class="circle-chevron hover:colors" aria-controls="${carouselId}" aria-label="${previousLabel}" disabled>
          <svg role="presentation" focusable="false" width="5" height="8" viewBox="0 0 5 8"><path d="m4.25 7-3-3 3-3" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>
        <button is="next-button" class="circle-chevron hover:colors" aria-controls="${carouselId}" aria-label="${nextLabel}">
          <svg role="presentation" focusable="false" width="5" height="8" viewBox="0 0 5 8"><path d="m.75 7 3-3-3-3" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>
      </div>`
    : '';
  return `
    <div class="v-stack gap-2.5 sm:gap-4">
      <div class="h-stack justify-between gap-4">
        ${arrows}
      </div>
      <scroll-carousel selector=".horizontal-product" id="${carouselId}" class="horizontal-product-list-carousel separate scroll-area bleed">
        <div class="horizontal-product-list separate"></div>
      </scroll-carousel>
    </div>`;
}

const CartRelatedRecommendations = class extends HTMLElement {
  constructor() {
    super();
    this._loadGeneration = 0;
    this._onCartChange = event => this._handleCartChange(event);
    this._revealObserver = null;
    this._curatedTemplate = undefined;
  }

  connectedCallback() {
    document.addEventListener('cart:change', this._onCartChange);
    this._loadWhenRendered();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this._onCartChange);
    this._revealObserver?.disconnect();
    this._revealObserver = null;
  }

  /**
   * Is this element rendered — i.e. is it and every ancestor free of `display: none`?
   *
   * That is the whole question, NOT whether the element is on screen. `checkVisibility()` answers
   * exactly it, so it is the primary test.
   *
   * The fallback walks ancestors rather than measuring a box, and that matters: this element
   * carries no `display` rule anywhere in the theme, so it is an inline box, and it is EMPTY until
   * `_load()` fills it (the curated <template> it ships with renders nothing). Whether an empty
   * inline element yields a client rect is engine-dependent, so a `getClientRects().length` test
   * could report a perfectly visible element as hidden — and because a false negative here means
   * `_load()` never runs, the shopper would get neither the dynamic rail nor the curated fallback
   * (both are rendered from inside `_load`). A silently feature-less drawer on one engine is
   * exactly the failure this shape avoids.
   *
   * Not `offsetParent`, which is also null for a `position: fixed` element.
   */
  _isRendered() {
    if (typeof this.checkVisibility === 'function') {
      return this.checkVisibility();
    }
    if (getComputedStyle(this).display === 'none') return false;
    for (let ancestor = this.parentElement; ancestor; ancestor = ancestor.parentElement) {
      if (getComputedStyle(ancestor).display === 'none') return false;
    }
    return true;
  }

  /**
   * Skip the fetches while nothing can be seen, and run them the moment something can.
   *
   * The cart-drawer section renders on EVERY page (it lives in overlay-group), so this element
   * connects inside a closed drawer on every page view. It still loaded: on an empty cart it seeds
   * from `theme:recently-viewed-products`, so a returning shopper paid up to MAX_SEEDS section
   * requests per page view for markup nobody had opened.
   *
   * Deliberately a layout test, not an IntersectionObserver. Viewport-gating would also defer the
   * rail until it scrolls into view inside the drawer's own scroller, which is a different feature
   * (lazy loading, with the pop-in that comes with it) and not what is broken here. Anything
   * currently visible keeps loading exactly as eagerly as before.
   */
  _loadWhenRendered() {
    if (this._isRendered()) {
      this._revealObserver?.disconnect();
      this._revealObserver = null;
      this._load();
      return;
    }
    this._watchForReveal();
  }

  /** Re-check once the drawer opens. */
  _watchForReveal() {
    if (this._revealObserver) return;

    const drawer = this.closest('cart-drawer, cart-notification-drawer');
    if (!drawer) return;

    this._revealObserver = new MutationObserver(() => this._loadWhenRendered());
    this._revealObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  _handleCartChange(event) {
    const cart = event.detail?.cart;
    if (!cart || !Array.isArray(cart.items)) return;
    // The cart-drawer morph leaves this element's data attributes untouched
    // (it's on the morphdom skip list), so we sync them manually from the new
    // cart before re-seeding the recommendations.
    this.dataset.productIds = cart.items
      .map(item => item.product_id)
      .filter(Boolean)
      .join(',');
    this.dataset.cartHandles = cart.items
      .map(item => item.handle)
      .filter(Boolean)
      .join(',');
    // Attributes are synced either way, so a hidden instance is already carrying the current cart
    // when it is later revealed — only the fetching waits.
    this._loadWhenRendered();
  }

  /**
   * Reload the rail, keeping the OLD cards on screen until the new ones are ready.
   *
   * This used to clear itself first (`this.innerHTML = ''` before the fetch), which collapsed the
   * rail to zero height for the whole round-trip — ~600ms measured on a live cart — and that
   * MOVED THE DRAWER'S SCROLL POSITION. The drawer's scroller cannot hold a `scrollTop` past its
   * content: with the rail gone, `scrollHeight` dropped by the rail's height, the browser clamped
   * `scrollTop` to the new maximum, and nothing put it back when the cards returned.
   *
   * Measured, adding a product from the rail with the drawer scrolled to the bottom:
   *
   *   before          scrollTop 373 / scrollHeight 910   (at the bottom)
   *   rail emptied    scrollTop 222 / scrollHeight 759   <- clamped, 151px lost
   *   cards back      scrollTop 222 / scrollHeight 1034  <- never restored
   *
   * So the shopper was left parked somewhere arbitrary, the distance depending on how tall the
   * rail happened to be — which reads as a half-finished scroll. It fired on ANY cart change, a
   * quantity bump included, so it also yanked the drawer under someone who had not asked for
   * anything to move.
   *
   * Keeping the old cards up is better than a reserved-height placeholder: there is no empty gap
   * to flash, and nothing to measure or release. A superseded load now leaves the previous cards
   * standing too (the generation guard returns before rendering), which is what the newest load
   * will replace anyway.
   */
  async _load() {
    const generation = ++this._loadGeneration;

    const seedIds = this._seedIds();
    if (!seedIds.length) {
      // Nothing to relate to: an empty cart and no browsing history either (or
      // storage is blocked). Show the curated list instead.
      this._renderCurated();
      return;
    }

    const htmlResponses = await this._fetchAll(seedIds);
    if (generation !== this._loadGeneration) return;

    const cards = this._parseCards(htmlResponses);
    if (!cards.length) {
      // Nothing related to the cart's recent items — every result was already
      // in the cart, or the fetches failed. Fall back to the merchant's curated
      // tiles, rendered through the very same carousel rather than as a second
      // list, rather than leaving the drawer with no recommendations at all.
      this._renderCurated();
      return;
    }

    this._render(cards);
  }

  /**
   * The merchant's curated tiles, server-rendered into a <template> inside this element.
   *
   * Cached on first read because `_render` clears this element — the template goes with it, and
   * every later load would find nothing. The cached node keeps its content alive regardless.
   * Nothing is lost by caching: the cart-drawer morph skips this element, so the template never
   * refreshes anyway — which is exactly why `_curatedCards` filters out what is already in the
   * cart itself, against the live `data-cart-handles`, instead of Liquid doing it once at render.
   */
  _curatedTemplateNode() {
    if (this._curatedTemplate === undefined) {
      this._curatedTemplate = this.querySelector('template[data-curated-recommendations]');
    }
    return this._curatedTemplate;
  }

  /**
   * Not sliced to MAX_CARDS: that is a budget for how much we ask the API for, not a cap on the
   * list the merchant curated by hand.
   */
  _curatedCards() {
    const template = this._curatedTemplateNode();
    if (!template) return [];

    const cartHandles = (this.dataset.cartHandles || '').split(',').filter(Boolean);
    return deduplicateByHandle(collectRecommendationCards(template.content), cartHandles);
  }

  /**
   * Fall back to the curated tiles — through `_render`, so the shopper gets ONE slider whichever
   * source filled it. An empty curated list (unset, or every product already in the cart) clears
   * the rail, which is the same "nothing to recommend" state as before.
   */
  _renderCurated() {
    const cards = this._curatedCards();
    if (cards.length) {
      this._render(cards);
      return;
    }
    this.innerHTML = '';
  }

  _seedIds() {
    const ids = (this.dataset.productIds || '').split(',').filter(Boolean);
    // FIRST N, not last. `data-product-ids` is `cart.items | map: 'product_id'`, and Shopify puts a
    // freshly added line at the FRONT of `cart.items` — verified against a live cart: adding a third
    // product to a two-line cart landed it at index 0. `slice(-MAX_SEEDS)` therefore seeded the rail
    // from the OLDEST lines in the cart, which is the opposite of what the comment here claimed and
    // the opposite of what predicts what the shopper is shopping for now.
    //
    // Same convention as `_recentlyViewedIds` below, which already slices from the front for the
    // same reason. `new Set` keeps first-occurrence order, so the newest survives de-duplication.
    if (ids.length) return [...new Set(ids)].slice(0, MAX_SEEDS);
    return this._recentlyViewedIds();
  }

  /**
   * Seeds for an EMPTY cart, where there are no line items to relate to.
   *
   * Falls back to what the shopper has been browsing. `js-variables.liquid`
   * writes every PDP view into `theme:recently-viewed-products`, newest FIRST
   * (it re-adds the id to move it, then reverses before storing), so slicing
   * from the front takes the most recent — the same convention the theme's own
   * recently-viewed section uses.
   *
   * Returns [] on any problem rather than throwing: Safari Private Browsing and
   * strict ITP throw SecurityError on the localStorage access itself, and a
   * blocked store is indistinguishable from "nothing viewed yet" here. Either
   * way the caller reveals the curated list, which is also what a first-time
   * visitor with no history gets.
   */
  _recentlyViewedIds() {
    let stored;
    try {
      stored = localStorage.getItem('theme:recently-viewed-products');
    } catch {
      return [];
    }
    if (!stored) return [];

    let parsed;
    try {
      parsed = JSON.parse(stored);
    } catch {
      return [];
    }
    if (!Array.isArray(parsed)) return [];

    const ids = parsed.map(id => String(id).trim()).filter(Boolean);
    return [...new Set(ids)].slice(0, MAX_SEEDS);
  }

  // Not `async`: it returns `Promise.all` directly, so the keyword bought nothing and tripped
  // require-await — which nothing noticed while this file was accidentally excluded from lint.
  // Each request already swallows its own failure, so the combined promise never rejects.
  _fetchAll(seedIds) {
    const base = `${window.Shopify?.routes?.root ?? '/'}recommendations/products`;
    return Promise.all(
      seedIds.map(productId =>
        fetch(
          `${base}?product_id=${productId}&section_id=cart-recommendation-item&intent=related&limit=${MAX_CARDS}`,
          { priority: 'low' },
        )
          .then(response => (response.ok ? response.text() : ''))
          .catch(() => ''),
      ),
    );
  }

  _parseCards(htmlResponses) {
    const cartHandles = (this.dataset.cartHandles || '').split(',').filter(Boolean);
    const allCardEntries = htmlResponses.filter(Boolean).flatMap(parseRecommendationCards);
    return deduplicateByHandle(allCardEntries, cartHandles).slice(0, MAX_CARDS);
  }

  _render(cards) {
    this._curatedTemplateNode(); // cache it before the clear below takes it out of the DOM
    // `_load` no longer clears up front, so this is where the previous rail goes — one frame
    // before its replacement is attached, not a round-trip earlier. Without it the carousel below
    // is APPENDED and the drawer shows two rails.
    this.innerHTML = '';

    cards.forEach(card => {
      applyCardBackground(card, this.dataset.cardBackground);
      applyCardTextColor(card, this.dataset.cardTextColor);
    });

    const carouselId = 'cart-related-recommendations-carousel';
    const themeStrings = window.themeVariables?.strings ?? {};

    // Build the carousel detached from the live DOM and seed the cards into it
    // before attaching. BaseCarousel reads and caches `items` in its
    // connectedCallback; if the cards aren't inside yet, the cache locks in as
    // empty and the arrow controls + selection tracking break.
    const wrapper = document.createElement('div');
    wrapper.innerHTML = buildCarouselShell(carouselId, themeStrings, cards.length > 1);
    const root = wrapper.firstElementChild;

    const sectionTitle = this.dataset.title;
    if (sectionTitle) {
      const titleParagraph = document.createElement('p');
      // Same class the Liquid-rendered lists use, so this JS-built carousel's
      // heading matches "AKTUELL BELIEBT" / "KATEGORIEN" instead of rendering
      // as unstyled sentence case.
      titleParagraph.className = 'cart-drawer__section-title';
      titleParagraph.textContent = sectionTitle;
      root.querySelector('.h-stack.justify-between').prepend(titleParagraph);
    }

    const productList = root.querySelector('.horizontal-product-list');
    cards.forEach(card => productList.appendChild(card));

    this.appendChild(root);
  }
};

if (!customElements.get('cart-related-recommendations')) {
  customElements.define('cart-related-recommendations', CartRelatedRecommendations);
}
